import {
  CreateFolderMediaHouseDocument,
  type CreateFolderMediaHouseMutation,
  type CreateFolderMediaHouseMutationVariables,
} from "@/generated/graphql";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiAlertCircle, FiFolder, FiX } from "react-icons/fi";

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId: string | null;
  mediaType: "image" | "video" | "audio";
}

export function CreateFolderModal({
  isOpen,
  onClose,
  parentId,
  mediaType,
}: CreateFolderModalProps) {
  const [folderName, setFolderName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Get the correct refetch query based on media type
  const getRefetchQuery = () => {
    if (mediaType === "image") return "GetMediaHouseFolders";
    if (mediaType === "video") return "GetMediaHouseFolders";
    return "GetMediaHouseFolders";
  };

  const [createFolder, { loading }] = useMutation<
    CreateFolderMediaHouseMutation,
    CreateFolderMediaHouseMutationVariables
  >(CreateFolderMediaHouseDocument, {
    refetchQueries: [getRefetchQuery()],
    onCompleted: () => {
      toast.success("Folder created successfully");
      setFolderName("");
      setError(null);
      onClose();
    },
    onError: (err) => {
      setError(err.message);
      toast.error(`Failed to create folder: ${err.message}`);
    },
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFolderName("");
      setError(null);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!folderName.trim()) {
      setError("Folder name is required");
      return;
    }

    await createFolder({
      variables: {
        input: {
          name: folderName.trim(),
          parentId,
          type: mediaType,
        },
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-folder-title"
    >
      <div
        className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent line at top */}
        <div className="h-1 bg-gradient-to-r from-primary-500 to-secondary-500" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-50 text-primary-600">
              <FiFolder className="h-5 w-5" />
            </div>
            <h2 id="create-folder-title" className="h6">
              Create New Folder
            </h2>
          </div>
          <IconButton
            onClick={onClose}
            variant="ghost"
            shape="circle"
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
            disabled={loading}
          >
            <FiX className="h-5 w-5" />
          </IconButton>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-6">
          <div className="mb-5">
            <label htmlFor="folder-name" className="mb-2 block label">
              Folder Name
            </label>
            <input
              id="folder-name"
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              placeholder="Enter folder name"
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-5 flex items-start gap-3 rounded-lg bg-error-50 p-3 text-sm text-error-700 border border-error-200">
              <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button onClick={onClose} variant="ghost" size="md" disabled={loading} className="px-4">
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="md" disabled={loading} className="px-5">
              {loading ? "Creating..." : "Create Folder"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
